import { Link } from "react-router-dom";
import { useCategories } from "../api/hooks";
import { categoryIcon, GridSkeleton, ErrorState, EmptyState } from "../components/States";
import { MaskedLine, Reveal } from "../components/Motion";
import { Seo } from "../components/Seo";

const CategoriesPage = () => {
    const { data: categories, isLoading, isError, refetch } = useCategories();

    return (
        <>
            <Seo
                title="Categories — SahiKhoj"
                description="Browse deals, coupons and offers by category on SahiKhoj — fashion, electronics, food, travel and more."
                path="/categories"
            />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        Browse
                    </p>
                    <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                        <MaskedLine delay={0.15}>
                            Every category. <span className="text-saffron">One khoj.</span>
                        </MaskedLine>
                    </h1>
                </div>
            </section>

            <section className="bg-cream py-14 sm:py-20" data-testid="categories-page">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    {isLoading && <GridSkeleton count={8} />}
                    {isError && <ErrorState onRetry={refetch} />}
                    {categories && categories.length === 0 && (
                        <EmptyState title="No categories yet" message="Categories will appear here as offers are added." />
                    )}
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        {(categories || []).map((c, i) => {
                            const Icon = categoryIcon(c.icon);
                            return (
                                <Reveal key={c.id} delay={i * 0.04}>
                                    <Link
                                        to={`/categories/${c.slug}`}
                                        data-testid={`category-card-${c.slug}`}
                                        className="group flex h-full flex-col items-start gap-4 rounded-2xl border border-ink/5 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                                    >
                                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-saffron-soft transition-colors duration-300 group-hover:bg-saffron">
                                            <Icon className="h-5 w-5 text-saffron transition-colors duration-300 group-hover:text-white" />
                                        </span>
                                        <span className="font-display text-lg font-medium tracking-tight text-ink">{c.name}</span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-smoke">
                                            {c.type === "travel" ? "Travel" : "Shopping"}
                                        </span>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
};

export default CategoriesPage;
